const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const ALLOWED_IMAGE_EXTENSIONS = /\.(jpe?g|png|webp|gif)$/i;
const MAX_IMAGE_SIZE_BYTES = 50 * 1024 * 1024;

export const initialCategoryForm = {
  title: "",
  key: "",
  description: "",
  max_members: "1",
  navigator_allowed: true,
  consent: "",
  route_id: "",
};

export const normalizeCategories = (data) => {
  if (Array.isArray(data)) return data;
  return data?.data || data?.categories || data?.items || [];
};

export const isJeepRallyAdmin = () => {
  try {
    const user = JSON.parse(sessionStorage.getItem("userDetails") || "{}");
    const role = String(user.role || user.roles || "")
      .toLowerCase()
      .trim();
    return role === "admin";
  } catch {
    return false;
  }
};

export const toCategoryKey = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
};

export const getCategoryApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  const message = data?.message || fallback;
  if (Array.isArray(data?.errors) && data.errors.length) {
    return `${message}: ${data.errors.join(", ")}`;
  }
  return message;
};

export const filterCategories = (categories, searchTerm) => {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return categories;

  return categories.filter((category) => {
    const title = category?.title?.toLowerCase() || "";
    const key = category?.key?.toLowerCase() || "";
    const description = category?.description?.toLowerCase() || "";
    const routeTitle = getCategoryRouteTitle(category).toLowerCase();
    return (
      title.includes(term) ||
      key.includes(term) ||
      description.includes(term) ||
      routeTitle.includes(term)
    );
  });
};

export const getCategoryRoute = (category) => {
  const route = category?.route_id || category?.route;
  if (!route) return null;
  if (typeof route === "string") return { _id: route };
  return route;
};

export const getCategoryRouteTitle = (category) => {
  const route = getCategoryRoute(category);
  if (!route) return "";
  return route.title || route.name || "";
};

export const getCategoryRouteSummary = (category) => {
  const route = getCategoryRoute(category);
  if (!route || (!route.title && !route.name && route.total_distance_km == null)) {
    return "No route assigned";
  }

  const parts = [route.title || route.name || "Route"];
  if (route.total_distance_km != null && route.total_distance_km !== "") {
    parts.push(`${route.total_distance_km} km`);
  }
  if (route.estimated_time) parts.push(route.estimated_time);
  return parts.join(" · ");
};

export const mapCategoryToForm = (category) => ({
  title: category?.title || "",
  key: category?.key || "",
  description: category?.description || "",
  max_members:
    category?.max_members === 0 || category?.max_members
      ? String(category.max_members)
      : "1",
  navigator_allowed: category?.navigator_allowed !== false,
  consent: category?.consent || "",
  route_id:
    typeof category?.route_id === "object"
      ? category.route_id?._id || category.route_id?.id || ""
      : category?.route_id || "",
});

export const validateCategoryImage = (file) => {
  if (!file) return "";

  const type = String(file.type || "").toLowerCase();
  const name = String(file.name || "");
  const typeOk = ALLOWED_IMAGE_TYPES.includes(type);
  const extOk = ALLOWED_IMAGE_EXTENSIONS.test(name);

  if (!typeOk && !extOk) {
    return "Only JPEG, PNG, WEBP, GIF images are allowed.";
  }

  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    return "Image must be 50MB or smaller.";
  }

  return "";
};

export const validateCategoryForm = (form, options = {}) => {
  const {
    existingCategories = [],
    currentId = null,
    imageFile = null,
    requireRouteId = false,
  } = options;
  const errors = {};

  if (requireRouteId && !form.route_id?.trim()) {
    errors.route_id = "Route is required.";
  }

  if (!form.title?.trim()) {
    errors.title = "Category title is required.";
  }

  const key = toCategoryKey(form.key);
  if (!key) {
    errors.key = "Category key is required.";
  } else {
    const duplicate = existingCategories.find((category) => {
      const sameKey = toCategoryKey(category?.key) === key;
      const categoryId = category?._id || category?.id;
      return sameKey && categoryId !== currentId;
    });
    if (duplicate) {
      errors.key = "Category key already exists.";
    }
  }

  if (form.max_members === "" || form.max_members === null || form.max_members === undefined) {
    errors.max_members = "max_members is required.";
  } else {
    const maxMembers = Number(form.max_members);
    if (!Number.isInteger(maxMembers)) {
      errors.max_members = "max_members is required.";
    } else if (maxMembers < 0) {
      errors.max_members = "max_members cannot be negative.";
    }
  }

  if (typeof form.navigator_allowed !== "boolean") {
    errors.navigator_allowed = "navigator_allowed is required.";
  }

  if (imageFile) {
    const imageError = validateCategoryImage(imageFile);
    if (imageError) errors.image = imageError;
  }

  return errors;
};

const hasFormDataEntries = (formData) => {
  if (typeof formData.entries !== "function") return true;
  return !formData.entries().next().done;
};

export const buildCategoryFormData = (form, options = {}) => {
  const { isEdit = false, initialForm = null, imageFile = null } = options;
  const formData = new FormData();

  const title = form.title.trim();
  const key = toCategoryKey(form.key);
  const description = form.description?.trim() || "";
  const consent = form.consent?.trim() || "";
  const maxMembers = String(Number(form.max_members));
  const navigatorAllowed = form.navigator_allowed ? "true" : "false";

  if (!isEdit) {
    formData.append("title", title);
    formData.append("key", key);
    formData.append("max_members", maxMembers);
    formData.append("navigator_allowed", navigatorAllowed);
    if (description) formData.append("description", description);
    if (consent) formData.append("consent", consent);
    if (form.route_id?.trim()) {
      formData.append("route_id", form.route_id.trim());
    }
    if (imageFile) formData.append("image", imageFile);
    return formData;
  }

  const initialTitle = initialForm?.title?.trim() || "";
  const initialKey = toCategoryKey(initialForm?.key);
  const initialDescription = initialForm?.description?.trim() || "";
  const initialConsent = initialForm?.consent?.trim() || "";
  const initialMaxMembers = String(Number(initialForm?.max_members));
  const initialNavigatorAllowed = initialForm?.navigator_allowed ? "true" : "false";

  if (title !== initialTitle) formData.append("title", title);
  if (key !== initialKey) formData.append("key", key);
  if (maxMembers !== initialMaxMembers) formData.append("max_members", maxMembers);
  if (navigatorAllowed !== initialNavigatorAllowed) {
    formData.append("navigator_allowed", navigatorAllowed);
  }
  if (description !== initialDescription) {
    formData.append("description", description);
  }
  if (consent !== initialConsent) {
    formData.append("consent", consent);
  }
  if (imageFile) formData.append("image", imageFile);

  // route_id is intentionally not included for edit for now.
  // If your API requires it on update, we can add it here.

  return hasFormDataEntries(formData) ? formData : null;
};
